import { useTranslation } from 'react-i18next'

import { CategoricalSharePayload, CategoricalSharePlotView } from '@digital-twins/deck-ui/plots/categorical-share'

import { QuickStudyRankingPlot, hasQuickStudyRankingPlot } from './QuickStudyRankingPlot'
import { Result } from './quickStudyTypes'

/**
 * A monadic yes / no or choice read drawn from the plots Python stamped per item
 * (W7 Q2, Q3), each item over the twins who saw it:
 * - yes / no: each item's yes share, `ranking-bars` on `ranking_plot`;
 * - single select: each item's answer split, one `stacked-100-bars` row per item;
 * - multi select: each item's picks, `grouped-bars` (shares of people).
 * The escape option is typed and never crowned (decision 13). Returns null when
 * the read carries no such plot (stored results keep the browser bars).
 */

export type QuickStudyMonadicResult = Pick<
  Result,
  'monadic' | 'monadic_boolean' | 'monadic_choice' | 'ranking_plot' | 'stacked_100_bars_plot' | 'grouped_bars_plot'
>

export interface QuickStudyMonadicPlotProps {
  result: QuickStudyMonadicResult
}

function isCategoricalPlot(plot: unknown, kind: 'stacked-100-bars' | 'grouped-bars'): boolean {
  if (typeof plot !== 'object' || plot === null) {
    return false
  }
  const payload = plot as { kind?: unknown; data?: { rows?: unknown } }
  return payload.kind === kind && typeof payload.data === 'object' && payload.data !== null && Array.isArray(payload.data.rows)
}

/** The stamped per-item split of a monadic choice read, if any. */
function monadicChoicePlotOf(result: QuickStudyMonadicResult): CategoricalSharePayload | null {
  if (!result.monadic || (result.monadic_choice?.length ?? 0) === 0) {
    return null
  }
  if (isCategoricalPlot(result.stacked_100_bars_plot, 'stacked-100-bars')) {
    return result.stacked_100_bars_plot as CategoricalSharePayload
  }
  if (isCategoricalPlot(result.grouped_bars_plot, 'grouped-bars')) {
    return result.grouped_bars_plot as unknown as CategoricalSharePayload
  }
  return null
}

function isMonadicBooleanPlot(result: QuickStudyMonadicResult): boolean {
  return Boolean(result.monadic) && (result.monadic_boolean?.length ?? 0) > 0 && hasQuickStudyRankingPlot(result)
}

/** Whether a monadic read can draw through `QuickStudyMonadicPlot` (a per-item plot is stamped). */
export function hasQuickStudyMonadicPlot(result: QuickStudyMonadicResult): boolean {
  return isMonadicBooleanPlot(result) || monadicChoicePlotOf(result) !== null
}

export function QuickStudyMonadicPlot(props: QuickStudyMonadicPlotProps) {
  const { result } = props

  const { t, i18n } = useTranslation('app')

  if (isMonadicBooleanPlot(result)) {
    return (
      <div data-testid="qs-monadic-boolean-plot">
        <QuickStudyRankingPlot result={result} testIdPrefix="qs-monadic-boolean-plot-bars" />
      </div>
    )
  }
  const choice = monadicChoicePlotOf(result)
  if (!choice) {
    return null
  }
  return (
    <div data-testid="qs-monadic-choice-plot">
      <CategoricalSharePlotView
        plot={choice}
        size="card"
        locale={i18n.language}
        testIdPrefix={`qs-monadic-choice-plot-${choice.kind}`}
        otherToggleLabels={{
          expand: (count: number) => t('studyReport.plotOther.expand', { count }),
          collapse: t('studyReport.plotOther.collapse'),
        }}
      />
    </div>
  )
}
